import type { ImageType } from '@components/Card/CardHeader/utilities';
import { CardLoader } from '@components/Card2/CardLoader';
import type { CardMode } from '@components/Card2/types';
import { CardFontLoader } from '@components/CardFontLoader';
import { cardEditContext } from '@context/card-edit-context';
import { cardModeContext } from '@context/card-mode-context';
import { getOrCreateContext } from '@context/singleton-manager';
import type { CardDetailsFragment } from '@graphql/fragments/cards/core.graphql';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { useCardAttributes } from '@hooks/use-card-attributes';
import type { SettableNode } from '@hooks/use-node';
import useSingletonContext from '@hooks/use-singleton-context';
import { hexToRGB, isHexValue } from '@modules/color-helpers';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { useContext } from 'react';
import { palettenext } from 'ui-lib/src/lib/color/palette-next';

import { useCardDrag, useCardSize } from '../../../hooks/card';
import { CardField } from '../CardField';
import CardHeader from '../CardHeader/CardHeader';
import { Name } from '../CardName';
import { Accreditations } from '../CardProperty/Accreditations';
import { CompanyName } from '../CardProperty/CompanyName';
import { Department } from '../CardProperty/Department';
import { Headline } from '../CardProperty/Headline';
import { JobTitle } from '../CardProperty/JobTitle';
import { CardFlavours } from '../types';
import styles from './Card.module.scss';

interface CardProps {
  createMode?: boolean;
  mode?: CardMode;
  forwardedRef?: (instance: HTMLElement | null) => void;
  headingNode?: SettableNode<HTMLHeadingElement> | undefined;
  loading?: boolean;
  /**
   * Use this to change the appearance of the card to suite a layout context:
   *
   * - `standalone` - the card will only take up it's specified min/max width/height.
   * - `fit` - the card expands to fill up parent container (like when Card is rendered inside phone frame).
   *
   * */
  appearance?: 'fit' | 'standalone';
  picturePreview?: ImageType | undefined;
  card: Maybe<CardDetailsFragment>;
  handleClose?: () => void;
  /** Disable drag and drop re-ordering of fields on a card */
  disableDragToReorder?: boolean;
  onBannerImageLoad?: () => void;
  onFloatingLeftImageLoad?: () => void;
  onFloatingRightImageLoad?: () => void;
}

const outlineContext = getOrCreateContext('card-outline');

/**
 * You might be wondering why I don't use forwardRef here, and instead pass the ref as a prop. The reason
 * is that NextJs dynamic components don't play nice with forwarding refs, I think it has something to do
 * with the component being wrapped inside another component to achieve dynamic rendering.
 */

export const Card: React.FC<CardProps> = ({
  createMode = false,
  mode = 'clickable',
  forwardedRef,
  headingNode,
  loading = false,
  appearance = 'standalone',
  picturePreview,
  card,
  disableDragToReorder,
  onBannerImageLoad,
  onFloatingLeftImageLoad,
  onFloatingRightImageLoad,
}) => {
  const cardFields = card?.fields;
  const cardProperties = card?.properties;

  const cardAttributes = useCardAttributes();
  const [outlineContextProps] = useSingletonContext();
  const { pendingCardField, flavour } = useContext(cardEditContext);

  const { handleDragStart, handleDragEnd } = useCardDrag(card);
  const { size, resizeRef } = useCardSize();

  const cardColor = cardProperties?.color?.value ?? palettenext.brand[400];

  const rgbColor =
    cardColor && isHexValue(cardColor) ? hexToRGB(cardColor, { tripletOnly: true }) : cardColor;

  const showLoadingPlaceholder = loading || !card;
  if (showLoadingPlaceholder) {
    return (
      <div ref={resizeRef} className={styles['card-measurer']} data-appearance={appearance}>
        <CardLoader appearance={appearance} />
      </div>
    );
  }

  const nameProperties = {
    first_name: card.properties?.first_name,
    honorific_prefix: card.properties?.honorific_prefix,
    honorific_suffix: card.properties?.honorific_suffix,
    last_name: card.properties?.last_name,
    maiden_name: card.properties?.maiden_name,
    middle_name: card.properties?.middle_name,
    preferred_name: card.properties?.preferred_name,
    pronouns: card.properties?.pronouns,
  };

  return (
    <cardModeContext.Provider value={{ mode }}>
      <outlineContext.Provider value={outlineContextProps}>
        <CardFontLoader
          primaryFontUrl={card?.primary_font?.fontUrl}
          secondaryFontUrl={card?.secondary_font?.fontUrl}
        >
          <div ref={resizeRef} className={styles['card-measurer']} data-appearance={appearance}>
            <div className={styles['card-container']} style={{ '--card-color': rgbColor }}>
              <section
                className={styles['card']}
                ref={forwardedRef}
                data-edit-mode={Boolean(mode === 'edit')}
                data-appearance={appearance}
                data-size={size}
              >
                <CardHeader
                  card={card}
                  isTemplate={flavour === CardFlavours.TEMPLATE}
                  appearance={appearance}
                  createMode={createMode}
                  node={headingNode}
                  picturePreview={picturePreview}
                  {...cardFields}
                  onBannerImageLoad={onBannerImageLoad}
                  onFloatingLeftImageLoad={onFloatingLeftImageLoad}
                  onFloatingRightImageLoad={onFloatingRightImageLoad}
                />
                <div className={styles['user-details']}>
                  <Name fields={nameProperties} cardId={card.id} />

                  <JobTitle property={card.properties?.job_title ?? undefined} cardId={card.id} />

                  <Department property={card.properties?.department ?? undefined} cardId={card.id} />

                  <CompanyName property={card.properties?.org_name ?? undefined} cardId={card.id} />

                  <Headline property={card.properties?.headline ?? undefined} cardId={card.id} />

                  <Accreditations property={card.properties?.accreditations ?? undefined} cardId={card.id} />
                </div>

                <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
                  <div className={styles['card-items-container']}>
                    <Droppable droppableId="card-fields">
                      {(provided, snapshot) => (
                        <ul
                          ref={provided.innerRef}
                          className={styles['card-items']}
                          data-dragging-over={snapshot.isDraggingOver}
                          {...provided.droppableProps}
                        >
                          {!cardAttributes.loading &&
                            cardFields?.map((field, index) => {
                              return (
                                <li key={field.id}>
                                  <CardField
                                    cardId={card.id}
                                    field={field}
                                    index={index}
                                    size={size}
                                    cardAttributesTypes={cardAttributes.cardAttributeTypes}
                                    isDragDisabled={disableDragToReorder}
                                  />
                                </li>
                              );
                            })}
                          {provided.placeholder}
                          {pendingCardField && !cardAttributes.loading && (
                            <li>
                              <CardField
                                cardId={card.id}
                                field={pendingCardField}
                                index={10}
                                size={size}
                                cardAttributesTypes={cardAttributes.cardAttributeTypes}
                              />
                            </li>
                          )}
                        </ul>
                      )}
                    </Droppable>
                  </div>
                </DragDropContext>
              </section>
            </div>
          </div>
        </CardFontLoader>
      </outlineContext.Provider>
    </cardModeContext.Provider>
  );
};
